import React, { useCallback, useState } from 'react';

import { RouteLocation } from 'types/common.consts';
import useMapboxRouteAnimation from '../../../../custom-hooks/mapbox/mapbox-route-tracker/use-mapbox-route-animation/use-mapbox-route-animation';
import useMapboxRoutePlayer from '../../../../custom-hooks/mapbox/mapbox-route-tracker/use-mapbox-route-player/use-mapbox-route-player';
import { Feature, LineString } from '@turf/helpers';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { Button } from 'components';
import './mapbox-route-tracker-player.scss';

interface Props {
	routeProps: RouteProps;
	profileImageProps: ProfileImageProps;
	mainSteps: number;
	colorProps: ColorProps;
	disabled: boolean;
}

interface RouteProps {
	routeLocations: RouteLocation[];
	routeIndex: number;
	routeFeature: Feature<LineString>;
	onRouteIndexChange: (newIndex) => void;
}

interface ProfileImageProps {
	onUpdateProfileImagePoint: () => void;
}

interface ColorProps {
	primaryColor: string;
	disabledColor: string;
}

const MapboxRouteTrackerPlayer: React.FC<Props> = props => {
	const { routeLocations: route, routeIndex, routeFeature, onRouteIndexChange } = props.routeProps;
	const { onUpdateProfileImagePoint } = props.profileImageProps;
	const { primaryColor, disabledColor } = props.colorProps;
	const { mainSteps, disabled } = props;
	const [isPlaying, setIsPlaying] = useState(false);

	const pauseRouteTracker = useCallback(() => {
		if (routeIndex >= route.length) {
			onRouteIndexChange(0);
		}
		setIsPlaying(false);
	}, [routeIndex, route]);

	const { animateRouteTrackerIfNeeded, isRouteIndexValid } = useMapboxRouteAnimation(
		isPlaying,
		routeIndex,
		routeFeature,
		onRouteIndexChange,
		onUpdateProfileImagePoint,
		pauseRouteTracker
	);

	const onRouteTrackerGotToEndOfRoute = () => {
		pauseRouteTracker();
		onRouteIndexChange(0);
	};
	const onRouteTrackerPlay = () => {
		onUpdateProfileImagePoint();
	};

	const onRouteTrackerStop = () => {
		onUpdateProfileImagePoint();
	};

	const { playRouteTracker } = useMapboxRoutePlayer(
		isPlaying,
		route,
		routeIndex,
		onRouteTrackerGotToEndOfRoute,
		onRouteTrackerPlay,
		onRouteTrackerStop
	);

	const onPlayClicked = () => {
		if (disabled) {
			return;
		}
		if (isPlaying) {
			pauseRouteTracker();
		} else {
			setIsPlaying(true);
			playRouteTracker();
			animateRouteTrackerIfNeeded();
		}
	};

	const stepForward = useCallback(() => {
		if (disabled) {
			return;
		}
		let newIndex = routeIndex + mainSteps;
		if (!isRouteIndexValid(newIndex)) {
			newIndex = route.length - 1;
		}
		onRouteIndexChange(newIndex);
	}, [routeIndex]);

	const stepBackward = useCallback(() => {
		if (disabled) {
			return;
		}
		let newIndex = routeIndex - mainSteps;
		if (newIndex < 0) {
			newIndex = 0;
		}
		onRouteIndexChange(newIndex);
	}, [routeIndex]);

	const color = disabled ? disabledColor : primaryColor;

	return (
		<div className="mapbox-route-tracker-player">
			<Button
				className="mapbox-route-tracker-player-button"
				data-test-id="backward-button"
				onClick={stepBackward}
				icon={
					<BringgIcon
						className="mapbox-route-tracker-player-icon"
						iconName={BringgFontIcons.ChevronsStart}
						color={color}
					/>
				}
				type="link"
			/>
			<Button
				className="mapbox-route-tracker-player-button"
				data-test-id="play-button"
				onClick={onPlayClicked}
				icon={
					<BringgIcon
						className="mapbox-route-tracker-player-icon"
						iconName={isPlaying ? BringgFontIcons.Pause : BringgFontIcons.Play}
						color={color}
					/>
				}
				type="link"
			/>
			<Button
				className="mapbox-route-tracker-player-button"
				data-test-id="forward-button"
				onClick={stepForward}
				icon={
					<BringgIcon
						className="mapbox-route-tracker-player-icon"
						iconName={BringgFontIcons.ChevronsEnd}
						color={color}
					/>
				}
				type="link"
			/>
		</div>
	);
};

export default MapboxRouteTrackerPlayer;
